import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { KIDS_BELTS, ADULT_BELTS } from '../lib/constants'

export default function LoginPage() {
  const [userType, setUserType] = useState(null) // null | 'gym' | 'fighter'
  const [mode, setMode] = useState('login') // 'login' | 'signup' | 'forgot'
  const [step, setStep] = useState(1) // for fighter signup: 1=code, 2=details

  // Shared fields
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  // Gym signup fields
  const [gymName, setGymName] = useState('')
  const [gymSlug, setGymSlug] = useState('')

  // Fighter signup fields
  const [inviteCode, setInviteCode] = useState('')
  const [foundClub, setFoundClub] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [parentEmail, setParentEmail] = useState('')
  const [fighterType, setFighterType] = useState('adult')
  const [beltIndex, setBeltIndex] = useState(0)
  const [done, setDone] = useState(false)

  const belts = fighterType === 'kids' ? KIDS_BELTS : ADULT_BELTS

  async function handleGymLogin(e) {
    e.preventDefault()
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    if (err) setError(err.message)
    setLoading(false)
  }

  async function handleForgotPassword(e) {
    e.preventDefault()
    if (!email) { setError('Please enter your email address'); return }
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (err) { setError(err.message) } else {
      setMessage('Password reset email sent! Check your inbox.')
    }
    setLoading(false)
  }

  async function handleGymSignup(e) {
    e.preventDefault()
    if (!gymName || !gymSlug || !email || !password) { setError('Please fill in all fields'); return }
    setLoading(true); setError('')
    const slug = gymSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-')
    const { data, error: authErr } = await supabase.auth.signUp({ email, password })
    if (authErr) { setError(authErr.message); setLoading(false); return }
    const { data: club, error: clubErr } = await supabase.from('clubs').insert({
      name: gymName, slug, country: 'HR'
    }).select().single()
    if (clubErr) { setError(clubErr.message); setLoading(false); return }
    await supabase.from('profiles').insert({
      id: data.user.id, club_id: club.id, role: 'admin', full_name: gymName
    })
    const code = gymName.replace(/\s+/g, '').toUpperCase().slice(0, 6) + Math.floor(100 + Math.random() * 900)
    await supabase.from('invite_codes').insert({ club_id: club.id, code })
    setLoading(false)
  }

  async function handleFighterLogin(e) {
    e.preventDefault()
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    if (err) setError(err.message)
    setLoading(false)
  }

  async function verifyInviteCode() {
    if (!inviteCode.trim()) { setError('Please enter an invite code'); return }
    setLoading(true); setError('')
    const { data: code } = await supabase
      .from('invite_codes')
      .select('*, clubs(*)')
      .eq('code', inviteCode.toUpperCase().trim())
      .eq('active', true)
      .single()
    if (!code) {
      const { data: clubBySlug } = await supabase.from('clubs').select('*').eq('slug', inviteCode.toLowerCase().trim()).single()
      if (clubBySlug) { setFoundClub(clubBySlug); setStep(2); setLoading(false); return }
      setError('Invalid invite code. Check with your gym coach.')
      setLoading(false); return
    }
    setFoundClub(code.clubs)
    setStep(2)
    setLoading(false)
  }

  async function handleFighterSignup(e) {
    e.preventDefault()
    // Validation
    if (!firstName || !lastName || !dateOfBirth) { setError('Please fill in all required fields'); return }
    if (fighterType === 'adult' && !phone) { setError('Phone number is required for adults'); return }
    if (fighterType === 'adult' && !email) { setError('Email is required for adults'); return }
    if (fighterType === 'kids' && !parentEmail) { setError('Parent email is required for kids'); return }
    if (!password) { setError('Please set a password'); return }

    setLoading(true); setError('')

    const signupEmail = fighterType === 'kids' ? parentEmail : email
    const { data, error: authErr } = await supabase.auth.signUp({ email: signupEmail, password })
    if (authErr) { setError(authErr.message); setLoading(false); return }

    const { data: fighter } = await supabase.from('fighters').insert({
      club_id: foundClub.id,
      first_name: firstName,
      last_name: lastName,
      type: fighterType,
      belt_index: parseInt(beltIndex),
      sessions_count: 0,
      belt_sessions: 0,
      country: 'HR',
      avatar_style: 'avatar',
      avatar_seed: Math.floor(Math.random() * 100),
      pending_approval: true,
      self_registered: true,
      email: fighterType === 'adult' ? email : null,
      phone: phone || null,
      date_of_birth: dateOfBirth || null,
      parent_email: fighterType === 'kids' ? parentEmail : null,
    }).select().single()

    if (fighter && data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        club_id: foundClub.id,
        role: 'athlete',
        full_name: firstName + ' ' + lastName,
        fighter_id: fighter.id,
      })
      await supabase.from('fighter_gym_history').insert({
        fighter_id: fighter.id, club_id: foundClub.id, is_current: true
      })
      const { data: techs } = await supabase.from('techniques').select('id')
      if (techs?.length) {
        await supabase.from('fighter_skills').insert(techs.map(t => ({ fighter_id: fighter.id, technique_id: t.id, stars: 0 })))
      }
    }
    setLoading(false)
    setDone(true)
  }

  // ── DONE SCREEN ─────────────────────────────────────────────
  if (done) return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
        <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Request sent!</div>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          Your profile at <strong style={{ color: '#fff' }}>{foundClub?.name}</strong> has been created.
          Your coach will verify your belt rank — you'll be able to log in once approved.
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'left', lineHeight: 1.7 }}>
          <div>📌 Belt submitted: <strong style={{ color: '#fff' }}>{belts[beltIndex]?.n}</strong></div>
          <div>⏳ Waiting for coach approval</div>
          <div>📧 Check your email to confirm your account</div>
        </div>
      </div>
    </Shell>
  )

  // ── ROLE SELECTOR ────────────────────────────────────────────
  if (!userType) return (
    <Shell>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>I am a...</div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: '1rem' }}>
        <button onClick={() => setUserType('gym')} style={roleBtn}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏢</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Gym / Coach</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Manage my gym and fighters</div>
        </button>
        <button onClick={() => setUserType('fighter')} style={roleBtn}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🥋</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Fighter</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>View my fighter card</div>
        </button>
      </div>
    </Shell>
  )

  // ── GYM FORGOT PASSWORD ──────────────────────────────────────
  if (userType === 'gym' && mode === 'forgot') return (
    <Shell onBack={() => { setMode('login'); setError(''); setMessage('') }}>
      <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Reset password</div>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 20 }}>Enter your email and we'll send you a reset link.</div>
      <form onSubmit={handleForgotPassword}>
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="coach@example.com" />
        {error && <ErrorBox>{error}</ErrorBox>}
        {message && <div style={{ color: '#80e8b0', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(26,112,64,0.2)', borderRadius: 8 }}>{message}</div>}
        <SubmitBtn loading={loading} label="Send reset link" />
      </form>
    </Shell>
  )

  // ── GYM LOGIN / SIGNUP ───────────────────────────────────────
  if (userType === 'gym') return (
    <Shell onBack={() => { setUserType(null); setError('') }}>
      <Tabs mode={mode} setMode={m => { setMode(m); setError('') }}
        labels={['Sign in', 'Create gym']} values={['login', 'signup']} />
      <form onSubmit={mode === 'login' ? handleGymLogin : handleGymSignup}>
        {mode === 'signup' && (
          <>
            <Field label="Gym name" value={gymName} onChange={v => setGymName(v)} placeholder="JJK Fortress" />
            <Field label="Gym URL slug" value={gymSlug}
              onChange={v => setGymSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="jjk-fortress" hint="Used in your invite link" />
          </>
        )}
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="coach@example.com" />
        <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
        {error && <ErrorBox>{error}</ErrorBox>}
        {message && <div style={{ color: '#80e8b0', fontSize: 13, marginBottom: 12 }}>{message}</div>}
        <SubmitBtn loading={loading} label={mode === 'login' ? 'Sign in' : 'Create gym account'} />
        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button type="button" onClick={() => { setMode('forgot'); setError('') }}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
              Forgot password?
            </button>
          </div>
        )}
      </form>
    </Shell>
  )

  // ── FIGHTER FORGOT PASSWORD ──────────────────────────────────
  if (userType === 'fighter' && mode === 'forgot') return (
    <Shell onBack={() => { setMode('login'); setError(''); setMessage('') }}>
      <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Reset password</div>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 20 }}>Enter your email and we'll send you a reset link.</div>
      <form onSubmit={handleForgotPassword}>
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="ivan@example.com" />
        {error && <ErrorBox>{error}</ErrorBox>}
        {message && <div style={{ color: '#80e8b0', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(26,112,64,0.2)', borderRadius: 8 }}>{message}</div>}
        <SubmitBtn loading={loading} label="Send reset link" />
      </form>
    </Shell>
  )

  // ── FIGHTER LOGIN / SIGNUP ───────────────────────────────────
  if (userType === 'fighter') return (
    <Shell onBack={() => { setUserType(null); setError(''); setStep(1); setFoundClub(null) }}>
      <Tabs mode={mode} setMode={m => { setMode(m); setError(''); setStep(1); setFoundClub(null) }}
        labels={['Sign in', 'Join a gym']} values={['login', 'signup']} />

      {mode === 'login' && (
        <form onSubmit={handleFighterLogin}>
          <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="ivan@example.com" />
          <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
          {error && <ErrorBox>{error}</ErrorBox>}
          <SubmitBtn loading={loading} label="Sign in" />
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button type="button" onClick={() => { setMode('forgot'); setError('') }}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
              Forgot password?
            </button>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            Use PIN login? Ask your coach for the gym PIN link.
          </div>
        </form>
      )}

      {mode === 'signup' && step === 1 && (
        <div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
            Enter the invite code from your gym, or scan the gym QR code.
          </div>
          <Field label="Invite code" value={inviteCode}
            onChange={v => setInviteCode(v.toUpperCase())}
            placeholder="e.g. JJKFOR123" hint="Get this from your coach" />
          {error && <ErrorBox>{error}</ErrorBox>}
          <button onClick={verifyInviteCode} disabled={loading} style={btnPrimary}>
            {loading ? 'Checking...' : 'Continue →'}
          </button>
        </div>
      )}

      {mode === 'signup' && step === 2 && foundClub && (
        <form onSubmit={handleFighterSignup}>
          <div style={{ background: 'rgba(24,96,168,0.2)', border: '1px solid rgba(24,96,168,0.4)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13, color: '#80c8ff' }}>
            Joining: <strong>{foundClub.name}</strong>
          </div>

          {/* Type selector */}
          <Field label="Type">
            <div style={{ display: 'flex', gap: 8 }}>
              {['adult', 'kids'].map(t => (
                <button key={t} type="button" onClick={() => { setFighterType(t); setBeltIndex(0) }} style={{
                  flex: 1, padding: '8px', border: `1px solid ${fighterType === t ? 'rgba(24,96,168,0.6)' : 'rgba(255,255,255,0.15)'}`,
                  borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: fighterType === t ? 600 : 400,
                  background: fighterType === t ? 'rgba(24,96,168,0.25)' : 'rgba(255,255,255,0.05)',
                  color: fighterType === t ? '#80c8ff' : 'rgba(255,255,255,0.6)'
                }}>{t === 'adult' ? '🧑 Adult' : '👦 Kids'}</button>
              ))}
            </div>
          </Field>

          {/* Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="First name *" value={firstName} onChange={setFirstName} placeholder="Ivan" />
            <Field label="Last name *" value={lastName} onChange={setLastName} placeholder="Horvat" />
          </div>

          {/* Date of birth — mandatory for all */}
          <Field label="Date of birth *" type="date" value={dateOfBirth} onChange={setDateOfBirth} />

          {/* Adult-only fields */}
          {fighterType === 'adult' && (
            <>
              <Field label="Phone number *" type="tel" value={phone} onChange={setPhone} placeholder="+385 91 234 5678" />
              <Field label="Email *" type="email" value={email} onChange={setEmail} placeholder="ivan@example.com" />
            </>
          )}

          {/* Kids fields */}
          {fighterType === 'kids' && (
            <Field label="Parent email *" type="email" value={parentEmail} onChange={setParentEmail} placeholder="parent@example.com" hint="Account will be created under parent's email" />
          )}

          {/* Belt */}
          <Field label="Current belt *">
            <select value={beltIndex} onChange={e => setBeltIndex(e.target.value)} style={selectStyle}>
              {belts.map((b, i) => <option key={i} value={i}>{b.n}</option>)}
            </select>
          </Field>

          {/* Password */}
          <Field label="Password *" type="password" value={password} onChange={setPassword} placeholder="••••••••" />

          <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid rgba(255,200,0,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: 'rgba(255,220,100,0.8)' }}>
            ⚠️ Your belt rank will be verified by your coach before you can check in.
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <SubmitBtn loading={loading} label="Request to join" />
          <button type="button" onClick={() => { setStep(1); setFoundClub(null); setError('') }}
            style={{ ...btnSecondary, width: '100%', marginTop: 8 }}>← Change gym</button>
        </form>
      )}
    </Shell>
  )

  return null
}

// ── SHARED COMPONENTS ────────────────────────────────────────────

function Shell({ children, onBack }) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0a1628 0%,#1a3a6a 50%,#0a1628 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI', system-ui, sans-serif", padding: '1.5rem' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>🥋</div>
          <div style={{ color: '#fff', fontSize: 26, fontWeight: 700 }}>MyAthleteCard</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)', borderRadius: 16, padding: '1.75rem', border: '1px solid rgba(255,255,255,0.12)' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
              ← Back
            </button>
          )}
          {children}
        </div>
      </div>
    </div>
  )
}

function Tabs({ mode, setMode, labels, values }) {
  return (
    <div style={{ display: 'flex', background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: 3, marginBottom: '1.5rem' }}>
      {values.map((v, i) => (
        <button key={v} onClick={() => setMode(v)} style={{ flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer', background: mode === v ? 'rgba(255,255,255,0.15)' : 'transparent', color: mode === v ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: mode === v ? 600 : 400, fontSize: 13, fontFamily: 'inherit' }}>
          {labels[i]}
        </button>
      ))}
    </div>
  )
}

function Field({ label, type = 'text', value, onChange, placeholder, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 5, fontWeight: 500 }}>{label}</label>
      {children || <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />}
      {hint && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>{hint}</div>}
    </div>
  )
}

function ErrorBox({ children }) {
  return <div style={{ color: '#ffaaaa', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: 'rgba(255,0,0,0.15)', borderRadius: 8 }}>{children}</div>
}

function SubmitBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1860a8,#0c4480)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
      {loading ? 'Please wait...' : label}
    </button>
  )
}

const roleBtn = { flex: 1, padding: '20px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', cursor: 'pointer', fontFamily: 'inherit', color: '#fff', textAlign: 'center' }
const btnPrimary = { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1860a8,#0c4480)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }
const btnSecondary = { padding: '9px', borderRadius: 9, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
const selectStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, outline: 'none' }
